import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import AdbIcon from '@mui/icons-material/Adb';
import { useNavigate } from 'react-router-dom';
import AppLauncher from 'src/components/app-launcher/AppLauncher';
import { applications } from 'src/application';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { LogoutUser } from 'src/redux/user/userReducer';

interface HeaderProps {
  pages: string[];
  links: string[];
  appName: string;
}

const settings = ['Profile', 'Dashboard', 'Logout'];

const Header: React.FC<HeaderProps> = ({ pages, links, appName }) => {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user);

  const userInitials = currentUser && currentUser.user && currentUser.user.username
  ? currentUser.user.username
      .split(' ') // Split the username into words
      .map((namePart) => namePart[0]?.toUpperCase()) // Get the first letter of each word
      .join('') // Join the initials into a single string
  : 'U'; 

  
    const handleSettingAction = (setting: string) => {
      switch (setting) {
        case 'Logout':
          dispatch(LogoutUser());
          navigate('/login');
          break;
        case 'Profile':
          navigate(`/profile`);
          break;
        case 'Dashboard':
          navigate('/dashboard');
          break;
        default:
          console.warn(`No action defined for ${setting}`);
      }
    };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            noWrap
            component="a"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'cursive',
              fontWeight: 'bold',
              fontSize: '18px',
              letterSpacing: '.1rem',
              color: '#fff',
              textDecoration: 'none',
              padding: '0 5px 0 5px',
            }}
          >
            {appName}
          </Typography>

          {/* Mobile Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page, index) => (
                <MenuItem
                  key={page}
                  onClick={() => {
                    handleCloseNavMenu();
                    navigate(links[index]);
                  }}
                >
                  <Typography textAlign="center">{page}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <AdbIcon sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />

          {}
          <AppLauncher applications={applications} appLauncherUrl={''} />
          <Box sx={{ flexGrow: 1, display: 'flex' }}>
            {pages.map((page, index) => (
              <Button
                key={page}
                onClick={() => navigate(links[index])}
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                {page}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
          <Tooltip title={currentUser ? `${currentUser.user.username}` : 'User'}>
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
              <Avatar
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  width: 40,
                  height: 40,
                }}
              >
                {userInitials}
              </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem
                key={setting}
                onClick={() => {
                  handleSettingAction(setting);
                  handleCloseUserMenu(); // Close the menu after an action
                }}
              >
                <Typography textAlign="center">{setting}</Typography>
              </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
